import React, {
  useState,
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import DialogModal from '../../../../components/DialogModal';
import { StyledButton } from '../../../../components/Button';
import LoadingBadge from '../../../../components/LoadingBadge';
import useToast from '../../../hooks/useToast';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import { CoachInfo as CoachInfoModel } from '../../../../Model/CoachInfo';
import CoachHistory, { HistoryEventType } from '../../../Model/CoachHistory';

import {
  Container,
  Title,
  StyledTextField,
} from './styles';
import texts from './texts.json';

const AddSocialHandlesModal = ({
  coachId,
  loggedInUserId,
  loggedInUserName,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [instagramHandle, setInstagramHandle] = useState('');
  const [tiktokHandle, setTiktokHandle] = useState('');
  const [youtubeHandle, setYoutubeHandle] = useState('');
  const [coachInfoDoc, setCoachInfoDoc] = useState(null);

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  // Fetch existing social handles when modal opens
  useEffect(() => {
    const fetchExistingHandles = async () => {
      try {
        const coachInfoDocuments = await CoachInfoModel.getCoachInfosByCoachId(coachId);
        const currentCoachInfoDoc = coachInfoDocuments?.docs?.find((doc) => doc.current);

        if (isComponentMountedRef.current) {
          setCoachInfoDoc(currentCoachInfoDoc || null);
          if (currentCoachInfoDoc) {
            setInstagramHandle(currentCoachInfoDoc.instagram);
            setTiktokHandle(currentCoachInfoDoc.tiktok);
            setYoutubeHandle(currentCoachInfoDoc.youtube);
          }
        }
      } catch (error) {
        showToast(format(texts.errorFetchingSocialHandles, { error: error.message }), { error: true });
      }
    };

    if (coachId) {
      fetchExistingHandles();
    }
  }, [coachId, isComponentMountedRef, showToast]);

  const handleSave = useCallback(async () => {
    if (!instagramHandle && !tiktokHandle && !youtubeHandle) {
      showToast(texts.pleaseEnterSocialHandles, { error: true });
      return;
    }

    try {
      setIsLoading(true);
      const cleanData = {
        instagram: instagramHandle || null,
        tiktok: tiktokHandle || null,
        youtube: youtubeHandle || null,
        updatedAt: new Date(),
        updatedBy: loggedInUserId,
      };

      if (coachInfoDoc) {
        // Update existing document with new social handles
        await coachInfoDoc.updateCoachInfos(cleanData);
      } else {
        // Create new coach info document with social handles
        const newData = {
          coachId,
          current: true,
          ...cleanData,
        };
        await CoachInfoModel.createCoachInfo(coachId, newData);
      }

      // Add history entry for social handles being added
      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.SOCIAL_HANDLES_ADDED,
        coachId,
        actionedBy: loggedInUserId,
        actionedByUserName: loggedInUserName,
      });
      showToast(texts.socialHandlesSavedSuccessfully);
      onClose();
    } catch (error) {
      showToast(format(texts.errorSavingSocialHandles, { error: error.message }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    coachId,
    instagramHandle,
    tiktokHandle,
    youtubeHandle,
    loggedInUserId,
    showToast,
    onClose,
    isComponentMountedRef,
    coachInfoDoc,
    loggedInUserName,
  ]);

  return (
    <>
      <DialogModal
        open
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <Container>
          <Title>{texts.title}</Title>
          <StyledTextField
            label={texts.instagramHandleLabel}
            variant="outlined"
            value={instagramHandle}
            onChange={(e) => setInstagramHandle(e.target.value)}
            fullWidth
            margin="normal"
            disabled={isLoading}
          />
          <StyledTextField
            label={texts.tiktokHandleLabel}
            variant="outlined"
            value={tiktokHandle}
            onChange={(e) => setTiktokHandle(e.target.value)}
            fullWidth
            margin="normal"
            disabled={isLoading}
          />
          <StyledTextField
            label={texts.youtubeHandleLabel}
            variant="outlined"
            value={youtubeHandle}
            onChange={(e) => setYoutubeHandle(e.target.value)}
            fullWidth
            margin="normal"
            disabled={isLoading}
          />
          <StyledButton
            onClick={handleSave}
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <LoadingBadge /> : texts.save}
          </StyledButton>
        </Container>
      </DialogModal>
    </>
  );
};

AddSocialHandlesModal.propTypes = {
  coachId: PropTypes.string.isRequired,
  loggedInUserId: PropTypes.string.isRequired,
  loggedInUserName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

AddSocialHandlesModal.defaultProps = {};

export default AddSocialHandlesModal;
